/**
 * EditableElement Component
 *
 * Wraps content to make it editable in edit mode.
 * Shows visual indicators and handles click selection.
 */

import { ReactNode, CSSProperties } from 'react';
import { EditableElement as EditableElementType } from '../types';
import { useEditableElement, useRenderer } from '../context';

// =============================================================================
// TYPES
// =============================================================================

interface EditableElementProps {
  element: EditableElementType;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  as?: keyof JSX.IntrinsicElements;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function EditableElement({
  element,
  children,
  className = '',
  style,
  as: Component = 'span',
}: EditableElementProps) {
  const { selectedElement } = useRenderer();
  const { isEditable, hasChanges, handleClick } = useEditableElement(element);

  const isSelected = selectedElement?.id === element.id;

  // Build classes
  const editClasses = isEditable
    ? [
        'relative',
        'cursor-pointer',
        'transition-all',
        'duration-150',
        // Hover state
        'hover:outline',
        'hover:outline-2',
        'hover:outline-offset-2',
        'hover:outline-blue-400',
        // Selected state
        isSelected ? 'outline outline-2 outline-offset-2 outline-blue-500' : '',
        // Changed indicator
        hasChanges ? 'bg-yellow-100/20' : '',
      ].filter(Boolean).join(' ')
    : '';

  // Combine classes
  const combinedClasses = [className, editClasses].filter(Boolean).join(' ');

  // Type assertion for dynamic component
  const Tag = Component as keyof JSX.IntrinsicElements;

  return (
    <Tag
      className={combinedClasses}
      style={style}
      onClick={isEditable ? handleClick : undefined}
      data-editable={isEditable ? 'true' : undefined}
      data-element-id={element.id}
      data-element-type={element.type}
    >
      {children}
      {isEditable && hasChanges && (
        <span
          className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full"
          title="Unsaved changes"
        />
      )}
    </Tag>
  );
}

// =============================================================================
// TEXT ELEMENT
// =============================================================================

interface EditableTextProps {
  element: EditableElementType;
  className?: string;
  style?: CSSProperties;
  as?: keyof JSX.IntrinsicElements;
}

export function EditableText({
  element,
  className = '',
  style,
  as = 'span',
}: EditableTextProps) {
  const { currentValue } = useEditableElement(element);

  // For text, value should be a string
  const text = Array.isArray(currentValue) ? currentValue.join(', ') : currentValue;

  return (
    <EditableElement element={element} className={className} style={style} as={as}>
      {text}
    </EditableElement>
  );
}

// =============================================================================
// IMAGE ELEMENT
// =============================================================================

interface EditableImageProps {
  element: EditableElementType;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  loading?: 'lazy' | 'eager';
}

export function EditableImage({
  element,
  alt,
  className = '',
  style,
  loading = 'lazy',
}: EditableImageProps) {
  const { currentValue, isEditable, hasChanges, handleClick } = useEditableElement(element);
  const { selectedElement } = useRenderer();

  const src = Array.isArray(currentValue) ? currentValue[0] : currentValue;
  const isSelected = selectedElement?.id === element.id;

  const editClasses = isEditable
    ? [
        'cursor-pointer',
        'transition-all',
        'duration-150',
        'hover:ring-4',
        'hover:ring-blue-400',
        'hover:ring-offset-2',
        isSelected ? 'ring-4 ring-blue-500 ring-offset-2' : '',
      ].filter(Boolean).join(' ')
    : '';

  return (
    <div
      className={`relative inline-block ${isEditable ? 'cursor-pointer' : ''}`}
      onClick={isEditable ? handleClick : undefined}
    >
      <img
        src={src}
        alt={alt ?? element.label}
        className={[className, editClasses].filter(Boolean).join(' ')}
        style={style}
        loading={loading}
        data-editable={isEditable ? 'true' : undefined}
        data-element-id={element.id}
        data-element-type="image"
      />
      {isEditable && hasChanges && (
        <span
          className="absolute top-2 right-2 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white shadow pointer-events-none"
          title="Unsaved changes"
        />
      )}
      {isEditable && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/30 transition-opacity pointer-events-none">
          <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-800">
            Click to change image
          </span>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LIST ELEMENT
// =============================================================================

interface EditableListProps {
  element: EditableElementType;
  className?: string;
  itemClassName?: string;
  as?: 'ul' | 'ol';
  renderItem?: (item: string, index: number) => ReactNode;
}

export function EditableList({
  element,
  className = '',
  itemClassName = '',
  as = 'ul',
  renderItem,
}: EditableListProps) {
  const { currentValue, isEditable, hasChanges, handleClick } = useEditableElement(element);
  const { selectedElement } = useRenderer();

  const items = Array.isArray(currentValue) ? currentValue : [currentValue];
  const isSelected = selectedElement?.id === element.id;

  const List = as;

  const editClasses = isEditable
    ? [
        'relative',
        'cursor-pointer',
        'transition-all',
        'duration-150',
        'hover:outline',
        'hover:outline-2',
        'hover:outline-offset-4',
        'hover:outline-blue-400',
        isSelected ? 'outline outline-2 outline-offset-4 outline-blue-500' : '',
      ].filter(Boolean).join(' ')
    : '';

  return (
    <List
      className={[className, editClasses].filter(Boolean).join(' ')}
      onClick={isEditable ? handleClick : undefined}
      data-editable={isEditable ? 'true' : undefined}
      data-element-id={element.id}
      data-element-type="list"
    >
      {items.map((item, index) => (
        <li key={index} className={itemClassName}>
          {renderItem ? renderItem(item, index) : item}
        </li>
      ))}
      {isEditable && hasChanges && (
        <span
          className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full"
          title="Unsaved changes"
        />
      )}
    </List>
  );
}

// =============================================================================
// LINK ELEMENT
// =============================================================================

interface EditableLinkProps {
  element: EditableElementType;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function EditableLink({
  element,
  children,
  className = '',
  style,
}: EditableLinkProps) {
  const { currentValue, isEditable, hasChanges, handleClick } = useEditableElement(element);
  const { selectedElement } = useRenderer();

  const href = Array.isArray(currentValue) ? currentValue[0] : currentValue;
  const isSelected = selectedElement?.id === element.id;

  const editClasses = isEditable
    ? [
        'relative',
        'cursor-pointer',
        'hover:outline',
        'hover:outline-2',
        'hover:outline-offset-2',
        'hover:outline-blue-400',
        isSelected ? 'outline outline-2 outline-offset-2 outline-blue-500' : '',
      ].filter(Boolean).join(' ')
    : '';

  if (isEditable) {
    // In edit mode, prevent navigation
    return (
      <span
        className={[className, editClasses].filter(Boolean).join(' ')}
        style={style}
        onClick={handleClick}
        data-editable="true"
        data-element-id={element.id}
        data-element-type="link"
        data-href={href}
      >
        {children}
        {hasChanges && (
          <span
            className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full"
            title="Unsaved changes"
          />
        )}
      </span>
    );
  }

  return (
    <a href={href} className={className} style={style}>
      {children}
    </a>
  );
}
